import { Component } from '@theme/component';

/**
 * The storefront wishlist: one store that every bookmark reads and writes.
 *
 * Phase 1 keeps the list in this browser's localStorage, so guests can save and
 * nothing leaves the device. Phase 2 adds a sync for signed-in customers (the
 * Forcast Multitool writing a `forcast.wishlist` customer metafield through an
 * app proxy) behind this same API, so no surface changes when it lands.
 *
 * Items are keyed on the Shopify product ID, never the handle: the August 2026
 * rename rewrote every handle once already. A colourway is its own product (a
 * combined-listing child), so a bookmark saves a style in one colour and the size
 * is chosen when it goes in the bag. The handle is kept alongside only because
 * the wishlist drawer fetches each item by URL.
 *
 * Who reads it:
 * - Liquid bookmarks (snippets/forcast-wishlist-button.liquid) are the
 *   <forcast-wishlist-button> element defined below.
 * - Snap cards (snap/src/lib/wishlist.js) call window.forcastWishlist and listen
 *   for `forcast:wishlist-change`.
 * - The header's dot keys off `data-forcast-wishlist` on <html>, which is out of
 *   reach of the header re-render that a cart change triggers.
 * - The header's wishlist drawer (assets/forcast-wishlist-drawer.js).
 *
 * Every save and removal is also published as a Shopify customer event
 * (`forcast:wishlist_add` / `forcast:wishlist_remove`), so a custom pixel can
 * forward it to marketing (the planned phase 3) without touching the theme.
 */

const STORAGE_KEY = 'forcast:wishlist';
/** Phase 2's list.product_reference metafield holds 128 items, so phase 1 stops there too. */
const MAX_ITEMS = 128;
const CHANGE_EVENT = 'forcast:wishlist-change';

/**
 * @typedef {object} WishlistItem
 * @property {string} id - Shopify product ID, digits only.
 * @property {string} handle - The product's handle when it was saved.
 * @property {number} at - When it was saved, in ms since the epoch.
 */

/**
 * What a surface knows about the product it is showing. `id` is required to
 * save; `handle` alone is enough to ask whether something is saved, which is
 * all a Snap card knows in the moment after a swatch switches its colour.
 *
 * @typedef {object} WishlistRef
 * @property {string | number} [id]
 * @property {string} [handle]
 * @property {string} [title] - Passed through to the analytics event only.
 */

/** @param {unknown} value */
function toProductId(value) {
  const id = String(value ?? '')
    .trim()
    .replace(/^gid:\/\/shopify\/Product\//, '');
  return /^\d+$/.test(id) ? id : '';
}

/** @param {WishlistRef | string | number} ref */
function toRef(ref) {
  return ref !== null && typeof ref === 'object' ? ref : { id: ref };
}

/** @returns {WishlistItem[]} Newest first, deduped, capped. */
function load() {
  let stored;
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    // Unreadable or blocked storage reads as an empty list rather than an error.
    return [];
  }

  const seen = new Set();
  const loaded = [];
  for (const entry of Array.isArray(stored?.items) ? stored.items : []) {
    const id = toProductId(entry?.id);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    loaded.push({ id, handle: typeof entry.handle === 'string' ? entry.handle : '', at: Number(entry.at) || 0 });
  }
  return loaded.slice(0, MAX_ITEMS);
}

/** @param {WishlistItem[]} next */
function persist(next) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, items: next }));
  } catch {
    // Private mode or a full quota: the list still holds for this page view.
  }
}

/** @type {WishlistItem[]} */
let items = load();

/** @param {WishlistRef} ref */
function find(ref) {
  const id = toProductId(ref.id);
  if (id) return items.find((item) => item.id === id);
  return ref.handle ? items.find((item) => item.handle === ref.handle) : undefined;
}

function stamp() {
  const root = document.documentElement;
  if (items.length) root.dataset.forcastWishlist = String(items.length);
  else delete root.dataset.forcastWishlist;
}

/**
 * Marks an element for one run of a CSS animation and unmarks it after, so the
 * animation belongs to this change alone rather than to every page that loads
 * in the same state. The timeout covers a reduced-motion visitor, whose
 * animation never starts and so never ends.
 *
 * @param {Element} element
 * @param {string} attribute
 */
function flash(element, attribute) {
  element.removeAttribute(attribute);
  // Restart an animation still running from the last change.
  if (element instanceof HTMLElement) void element.offsetWidth;
  element.setAttribute(attribute, '');
  const clear = () => element.removeAttribute(attribute);
  element.addEventListener('animationend', clear, { once: true });
  setTimeout(clear, 600);
}

/**
 * @param {'add' | 'remove'} action
 * @param {WishlistItem} item
 * @param {WishlistRef} ref
 */
function publish(action, item, ref) {
  try {
    window.Shopify?.analytics?.publish?.(`forcast:wishlist_${action}`, {
      productId: item.id,
      handle: item.handle,
      title: ref.title || '',
    });
  } catch {
    // Analytics is best effort; it must never stop a bookmark from working.
  }
}

/**
 * @param {'add' | 'remove' | 'sync'} action - `sync` means the whole list may
 *   have changed underneath the page (first load, another tab, a back/forward
 *   restore), so a listener should re-read rather than patch.
 * @param {WishlistItem | null} item
 * @param {WishlistRef} [ref] - The shopper's own action, published to analytics;
 *   left out for anything they did not do themselves.
 */
function emit(action, item, ref) {
  stamp();
  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, { detail: { action, item: item ? { ...item } : null, count: items.length } })
  );
  if (item && ref && action !== 'sync') publish(action, item, ref);
}

const store = {
  /** @returns {WishlistItem[]} Newest first. */
  items: () => items.map((item) => ({ ...item })),

  count: () => items.length,

  /** @param {WishlistRef | string | number} ref */
  has: (ref) => Boolean(find(toRef(ref))),

  /**
   * @param {WishlistRef | string | number} ref
   * @returns {boolean} Whether it was newly saved.
   */
  add(ref) {
    const product = toRef(ref);
    const id = toProductId(product.id);
    if (!id) return false;

    const handle = typeof product.handle === 'string' ? product.handle : '';
    const existing = items.find((item) => item.id === id);
    if (existing) {
      // Already saved: keep its place in the list, but follow a renamed handle
      // so the wishlist drawer keeps finding it.
      if (handle && existing.handle !== handle) {
        existing.handle = handle;
        persist(items);
      }
      return false;
    }

    const item = { id, handle, at: Date.now() };
    const wasEmpty = items.length === 0;
    items = [item, ...items].slice(0, MAX_ITEMS);
    persist(items);
    emit('add', item, product);
    // The first save brings the header's dot in the way a first add brings in
    // the bag's (snippets/header-actions.liquid).
    if (wasEmpty) flash(document.documentElement, 'data-forcast-wishlist-appear');
    return true;
  },

  /**
   * @param {WishlistRef | string | number} ref
   * @param {{ publish?: boolean }} [options] - `publish: false` for housekeeping
   *   (the wishlist drawer dropping a product that no longer exists), which
   *   the drawer still hears about but analytics should not count as the
   *   shopper's.
   * @returns {boolean} Whether anything was removed.
   */
  remove(ref, { publish = true } = {}) {
    const product = toRef(ref);
    const item = find(product);
    if (!item) return false;

    items = items.filter((entry) => entry !== item);
    persist(items);
    emit('remove', item, publish ? product : undefined);
    return true;
  },

  /**
   * @param {WishlistRef | string | number} ref
   * @returns {boolean} Whether the product is saved afterwards.
   */
  toggle(ref) {
    const product = toRef(ref);
    if (find(product)) {
      store.remove(product);
      return false;
    }
    return store.add(product);
  },
};

window.forcastWishlist = store;

// Another tab saved or removed something. (Not fired in the tab that wrote.)
window.addEventListener('storage', (event) => {
  if (event.key !== null && event.key !== STORAGE_KEY) return;
  items = load();
  emit('sync', null);
});

// A back/forward restore brings the page back as it was, possibly from before
// a bookmark on the next page was tapped. No storage event covers same-tab writes.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  items = load();
  emit('sync', null);
});

// Anything that rendered before this module ran (a Snap card) catches up here.
emit('sync', null);

/**
 * A bookmark rendered by Liquid (snippets/forcast-wishlist-button.liquid).
 *
 * The markup ships unpressed, because Liquid cannot see this browser's list,
 * and the element sets the real state when it connects. A Component rather than
 * a plain element so a morph re-checks it too: a colour change on the PDP
 * morphs <main> to the sibling product, rewriting data-product-id and resetting
 * aria-pressed to the server's "false", and updatedCallback puts it right in the
 * same task, before anything paints.
 */
class ForcastWishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Bound on the element rather than the <button>, so it survives a morph
    // that replaces the button.
    this.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  /** @returns {WishlistRef} */
  get #product() {
    return {
      id: this.dataset.productId,
      handle: this.dataset.productHandle,
      title: this.dataset.productTitle,
    };
  }

  #sync = () => {
    this.querySelector('button')?.setAttribute('aria-pressed', String(store.has(this.#product)));
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const toggle = event.target instanceof Element ? event.target.closest('button') : null;
    if (!toggle || !this.contains(toggle)) return;

    // The bookmark sits beside links (a card's title, the full-card link), so the
    // click is the bookmark's alone.
    event.preventDefault();
    event.stopPropagation();
    if (store.toggle(this.#product)) flash(toggle, 'data-wishlist-pop');
  };
}

if (!customElements.get('forcast-wishlist-button')) {
  customElements.define('forcast-wishlist-button', ForcastWishlistButton);
}
